import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  Signal,
  signal,
} from '@angular/core';

import {
  BooleanTypeSelectItemsProviderService,
  DrawerContentComponent,
  DrawerTitleComponent,
  Icon,
  PrimengDrawerDirective,
  TableBuilderFactory,
  TableComponent,
  TableConfig,
  TableDataPayload,
  TableLazyLoad,
  TableService,
  ToastService,
} from '@web/shared/ui';
import { AdminSubscriptionListCustomCellTemplateComponent } from '../../components/admin-subscription-list-custom-cell-template/admin-subscription-list-custom-cell-template.component';
import { HttpErrorService, Route, RouteParam } from '@web/common';
import { Observable } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { AdminSubscriptionCollectionFilter } from '../../models/admin-subscription-collection-filter';
import { AdminSubscriptionListItemAction } from '../../enums/admin-subscription-list-item-action';
import { AdminSubscriptionCollectionItem } from '../../models/admin-subscription-collection-item';
import { AdminSubscriptionDataSourceService } from '../../services';
import { SubscriptionStatusProviderService } from '@web/shared/module/shop';
import { Drawer } from 'primeng/drawer';
import { AdminSubscriptionBillingAddressEditComponent } from '../admin-subscription-billing-address-edit/admin-subscription-billing-address-edit.component';
import { Guid, isNotNil, Nillable } from '@web/utils';
import { AdminSubscriptionShippingAddressEditComponent } from '../admin-subscription-shipping-address-edit/admin-subscription-shipping-address-edit.component';
import { AdminSubscriptionListActionsComponent } from '../admin-subscription-list-actions/admin-subscription-list-actions.component';
import { AdminSubscriptionAfterActionExecuteEvent } from '../../events/admin-subscription-after-action-execute-event';
import { AdminSubscriptionEditComponent } from '../admin-subscription-edit/admin-subscription-edit.component';

@Component({
  selector: 'app-admin-subscription-list',
  imports: [
    TableComponent,
    AdminSubscriptionListCustomCellTemplateComponent,
    Drawer,
    DrawerTitleComponent,
    DrawerContentComponent,
    AdminSubscriptionBillingAddressEditComponent,
    PrimengDrawerDirective,
    AdminSubscriptionShippingAddressEditComponent,
    AdminSubscriptionListActionsComponent,
    AdminSubscriptionEditComponent,
  ],
  templateUrl: './admin-subscription-list.component.html',
  styleUrl: './admin-subscription-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TableService],
})
export class AdminSubscriptionListComponent implements OnInit {
  /* prettier-ignore */
  protected readonly selectedId = signal<Nillable<Guid>>(null);
  /* prettier-ignore */
  protected readonly billingAddressEditVisibility = this.billingAddressEditVisibilitySignal();
  /* prettier-ignore */
  protected readonly shippingAddressEditVisibility = this.shippingAddressEditVisibilitySignal();
  /* prettier-ignore */
  protected readonly subscriptionEditVisibility = this.subscriptionEditVisibilitySignal();

  protected config!: TableConfig<
    AdminSubscriptionCollectionItem,
    AdminSubscriptionCollectionFilter,
    AdminSubscriptionListItemAction
  >;
  private readonly billingAddressEdit = signal(false);
  private readonly subscriptionEdit = signal(false);
  private readonly shippingAddressEdit = signal(false);

  /* prettier-ignore */
  private readonly builder = inject(TableBuilderFactory<AdminSubscriptionCollectionItem, AdminSubscriptionCollectionFilter, AdminSubscriptionListItemAction>).create();
  /* prettier-ignore */
  private readonly subscriptionDataSource = inject(AdminSubscriptionDataSourceService);
  /* prettier-ignore */
  private readonly booleanTypeSelectItemsProvider = inject(BooleanTypeSelectItemsProviderService);
  private readonly table = inject(TableService);
  private readonly toast = inject(ToastService);
  private readonly httpError = inject(HttpErrorService);
  private readonly statusProvider = inject(SubscriptionStatusProviderService);

  public readonly customShippingAddressEditDialogCloseEvent = () => {
    this.clearSelectedId();
    this.shippingAddressEdit.set(false);
  };

  public readonly customEditDialogCloseEvent = () => {
    this.clearSelectedId();
    this.subscriptionEdit.set(false);
  };

  public readonly customBillingAddressEditDialogCloseEvent = () => {
    this.clearSelectedId();
    this.billingAddressEdit.set(false);
  };

  public ngOnInit(): void {
    this.buildTable();
  }

  public handleBillingAddressSubmitEvent(): void {
    this.clearSelectedId();
    this.billingAddressEdit.set(false);
    this.refresh();
  }

  public handleShippingAddressSubmitEvent(): void {
    this.clearSelectedId();
    this.shippingAddressEdit.set(false);
    this.refresh();
  }

  public handleSubscriptionEditSubmitEvent(): void {
    this.clearSelectedId();
    this.subscriptionEdit.set(false);
    this.refresh();
  }

  public handleAfterActionExecuteEvent(
    event: AdminSubscriptionAfterActionExecuteEvent,
  ): void {
    switch (event.action) {
      default: {
        this.refresh();
      }
    }
  }

  private buildTable(): void {
    this.config = this.builder
      .dataKey('id')
      .filters((builder) => [builder.initText('number').build()])
      .itemActions((builder) => [
        builder
          .initRouterLink(AdminSubscriptionListItemAction.Preview)
          .icon(Icon.Eye)
          .tooltip('Podgląd')
          .visibility((item) => item.canBeViewed)
          .routerLink(
            (provider) => (item) =>
              provider.routerLink(Route.SubscriptionDetails, {
                [RouteParam.Guid]: item.id,
              }),
          )
          .build(),

        builder
          .initMore(AdminSubscriptionListItemAction.More)
          .visibility((item) => this.visibilityForMoreItemAction(item))
          .children((builder) => [
            builder
              .initCommand(AdminSubscriptionListItemAction.Process)
              .icon(Icon.ArrowRepeat)
              .tooltip('Przetwarzaj subskrypcję')
              .visibility((item) => item.canBeProcessed)
              .command((model) => this.process(model))
              .build(),

            builder
              .initCommand(AdminSubscriptionListItemAction.Edit)
              .icon(Icon.Pencil)
              .tooltip('Edytuj')
              .visibility((item) => item.canBeUpdated)
              .command((item) => this.editSubscription(item))
              .build(),

            builder
              .initCommand(AdminSubscriptionListItemAction.BillingAddressEdit)
              .icon(Icon.CashCoin)
              .tooltip('Edytuj adres rozliczeniowy')
              .visibility((item) => item.canBillingAddressBeUpdated)
              .command((item) => this.editBillingAddress(item))
              .build(),

            builder
              .initCommand(AdminSubscriptionListItemAction.ShippingAddressEdit)
              .icon(Icon.Truck)
              .tooltip('Edytuj adres dostawy')
              .visibility((item) => item.canShippingAddressBeUpdated)
              .command((item) => this.editShippingAddress(item))
              .build(),

            builder
              .initCommand(AdminSubscriptionListItemAction.Suspend)
              .icon(Icon.PauseCircle)
              .tooltip('Wstrzymaj')
              .visibility((item) => item.canBeSuspended)
              .command((model) => this.suspend(model))
              .build(),

            builder
              .initCommand(AdminSubscriptionListItemAction.Resume)
              .icon(Icon.PlayCircle)
              .tooltip('Wznów')
              .visibility((item) => item.canBeResumed)
              .command((model) => this.resume(model))
              .build(),

            builder
              .initCommand(AdminSubscriptionListItemAction.Cancel)
              .icon(Icon.XCircle)
              .tooltip('Anuluj')
              .visibility((item) => item.canBeCancelled)
              .command((model) => this.cancel(model))
              .build(),
          ])
          .build(),
      ])
      .filters((builder) => [
        builder //
          .initText('number')
          .build(),

        builder //
          .initText('subscriber')
          .build(),

        builder //
          .initText('totalPrice')
          .build(),

        builder
          .initMultiSelect('status')
          .dictionary(() => this.statusProvider.selectItems())
          .build(),

        builder //
          .initSelect('isLocked')
          .dictionary(() => this.booleanTypeSelectItemsProvider.selectItems())
          .build(),

        builder //
          .initDateRange('createdAt')
          .build(),

        builder //
          .initDateRange('lastPaymentDate')
          .build(),

        builder //
          .initDateRange('nextPaymentDate')
          .build(),
      ])
      .columns((builder) => [
        builder //
          .initText('number')
          .label('Numer')
          .width('10rem')
          .sortKey('number')
          .filterKey('number')
          .build(),

        builder //
          .initCustom('subscriber')
          .label('Subskrybent')
          .width('20rem')
          .sortKey('subscriber')
          .filterKey('subscriber')
          .build(),

        builder //
          .initCustom('status')
          .width('10rem')
          .label('Status')
          .sortKey('status')
          .filterKey('status')
          .build(),

        builder //
          .initBoolean('isLocked')
          .width('10rem')
          .label('Przetwarzanie')
          .sortKey('isLocked')
          .filterKey('isLocked')
          .build(),

        builder //
          .initCustom('totalPrice')
          .label('Kwota')
          .width('12rem')
          .sortKey('totalPrice')
          .filterKey('totalPrice')
          .build(),

        builder //
          .initDateTime('createdAt')
          .label('Data utworzenia subskrypcji')
          .sortKey('createdAt')
          .filterKey('createdAt')
          .build(),

        builder //
          .initDate('lastPaymentDate')
          .width('14rem')
          .label('Data ostatniej płatności')
          .sortKey('lastPaymentDate')
          .filterKey('lastPaymentDate')
          .build(),

        builder //
          .initCustom('nextPaymentDate')
          .width('14rem')
          .label('Data następnej płatności')
          .sortKey('nextPaymentDate')
          .filterKey('nextPaymentDate')
          .build(),

        builder //
          .initCustom('cardNumber')
          .width('12rem')
          .sortKey('cardExpiryDate')
          .label('Karta płatnicza')
          .build(),
      ])
      .dataSource({
        fetch: (
          lazyLoad: TableLazyLoad<AdminSubscriptionCollectionFilter>,
        ): Observable<TableDataPayload<AdminSubscriptionCollectionItem>> =>
          this.subscriptionDataSource.fetchCollection(lazyLoad),
      })
      .build();
  }

  private editShippingAddress(item: AdminSubscriptionCollectionItem): void {
    this.selectedId.set(item.id);
    this.shippingAddressEdit.set(true);
  }

  private editBillingAddress(item: AdminSubscriptionCollectionItem): void {
    this.selectedId.set(item.id);
    this.billingAddressEdit.set(true);
  }

  private editSubscription(item: AdminSubscriptionCollectionItem): void {
    this.selectedId.set(item.id);
    this.subscriptionEdit.set(true);
  }

  private clearSelectedId(): void {
    this.selectedId.set(null);
  }

  private billingAddressEditVisibilitySignal(): Signal<boolean> {
    return computed(
      () => isNotNil(this.selectedId()) && this.billingAddressEdit(),
    );
  }

  private subscriptionEditVisibilitySignal(): Signal<boolean> {
    return computed(
      () => isNotNil(this.selectedId()) && this.subscriptionEdit(),
    );
  }

  private shippingAddressEditVisibilitySignal(): Signal<boolean> {
    return computed(
      () => isNotNil(this.selectedId()) && this.shippingAddressEdit(),
    );
  }

  private cancel(model: AdminSubscriptionCollectionItem): void {
    this.subscriptionDataSource.confirmAndCancelById(model.id).subscribe({
      next: () => this.refresh(),
      error: (error) => this.showFailureToast(error),
    });
  }

  private suspend(model: AdminSubscriptionCollectionItem): void {
    this.subscriptionDataSource.confirmAndSuspendById(model.id).subscribe({
      next: () => this.refresh(),
      error: (error) => this.showFailureToast(error),
    });
  }

  private resume(model: AdminSubscriptionCollectionItem): void {
    this.subscriptionDataSource.confirmAndResumeById(model.id).subscribe({
      next: () => this.refresh(),
      error: (error) => this.showFailureToast(error),
    });
  }

  private process(model: AdminSubscriptionCollectionItem): void {
    this.subscriptionDataSource.confirmAndProcessById(model.id).subscribe({
      next: () => this.refresh(),
      error: (error) => this.showFailureToast(error),
    });
  }

  private showFailureToast(error: HttpErrorResponse): void {
    this.toast.show((builder) =>
      builder
        .initError(this.httpError.extract(error).multiLineMessages())
        .build(),
    );
  }

  private refresh(): void {
    this.table.refresh();
  }

  private visibilityForMoreItemAction(
    item: AdminSubscriptionCollectionItem,
  ): boolean {
    return [
      item.canBillingAddressBeUpdated,
      item.canBeCancelled,
      item.canShippingAddressBeUpdated,
      item.canBeProcessed,
      item.canBeSuspended,
      item.canBeResumed,
    ].some(Boolean);
  }
}
